import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import ModalComponent from "../../components/modals";
import LaddaButton from "../../components/buttons/laddaButton";
import SimpleTable from "../../components/tables/simpleTable";
import FormTextField from "../../components/textField/formTextField";
import { icons } from "../../constants";
import { callApiAsync, getInterestedPartiesUpdateHistory } from "../../services/apiService";

const UPDATE_HISTORY_PAGE_SIZE = 8;

const formatUpdateHistoryDatePart = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw);
  const head = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}/.test(head)) return head.slice(0, 10);
  return head.slice(0, 10);
};

const formatUpdateHistoryTimePart = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw);
  const m = s.match(/(\d{1,2}:\d{2}(?::\d{2})?)/);
  if (m) {
    const t = m[1];
    return (t.length === 5 ? `${t}:00` : t).slice(0, 8);
  }
  return null;
};

const parseChangedFields = (description) => {
  if (description == null || description === "") return [];
  const raw = String(description);

  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.format === "lifecycle_v1") {
      if (parsed.action === "create") return ["Creación de la parte interesada"];
      if (parsed.action === "delete") return ["Eliminación de la parte interesada"];
    }
    if (
      parsed &&
      parsed.format === "changed_fields_v1" &&
      Array.isArray(parsed.changed_fields)
    ) {
      return parsed.changed_fields
        .map((x) => x?.label || x?.field || "")
        .filter(Boolean);
    }
  } catch (e) {
    // legado
  }

  const legacy = raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("•"))
    .map((line) => {
      const noBullet = line.replace(/^•\s*/, "");
      const labelPart = noBullet.split(":")[0] || "";
      return labelPart.trim();
    })
    .filter(Boolean);

  if (legacy.length > 0) return legacy;
  return [raw];
};

/**
 * Modal aislado para no re-renderizar la pantalla completa al escribir en el filtro
 * (mismo criterio que modales tipo indicatorAnalisisModal).
 */
const InterestedPartiesUpdateHistoryModal = ({
  open,
  onClose,
  processId,
  parties = [],
  processRowData,
  notify,
  canEditFromHistory
}) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterApplied, setFilterApplied] = useState("");

  const { control, getValues, reset } = useForm({
    defaultValues: { historyPartySearch: "" }
  });

  const fetchUpdateHistory = useCallback(
    (pageNum, searchText) => {
      if (processId == null) return;
      const q = String(searchText ?? "").trim();
      callApiAsync(
        () =>
          getInterestedPartiesUpdateHistory(processId, {
            page: pageNum,
            limit: UPDATE_HISTORY_PAGE_SIZE,
            search: q || undefined
          }),
        (payload) => {
          if (Array.isArray(payload)) {
            setRows(payload);
            setTotal(payload.length);
            return;
          }
          const list = payload?.data;
          const t = payload?.total;
          setRows(Array.isArray(list) ? list : []);
          setTotal(typeof t === "number" ? t : 0);
        }
      );
    },
    [processId]
  );

  useEffect(() => {
    if (!open) {
      reset({ historyPartySearch: "" });
      setFilterApplied("");
      setPage(1);
      setRows([]);
      setTotal(0);
      return;
    }
    if (processId == null) return;
    setPage(1);
    fetchUpdateHistory(1, "");
  }, [open, processId, fetchUpdateHistory, reset]);

  const handleApplyFilter = useCallback(() => {
    const q = getValues("historyPartySearch").trim();
    setFilterApplied(q);
    setPage(1);
    fetchUpdateHistory(1, q);
  }, [getValues, fetchUpdateHistory]);

  const handleClearFilter = useCallback(() => {
    reset({ historyPartySearch: "" });
    setFilterApplied("");
    setPage(1);
    fetchUpdateHistory(1, "");
  }, [reset, fetchUpdateHistory]);

  const handlePageChange = useCallback(
    (newPage) => {
      setPage(newPage);
      fetchUpdateHistory(newPage, filterApplied);
    },
    [fetchUpdateHistory, filterApplied]
  );

  const handleViewPartyFromHistory = useCallback(
    (histRow) => {
      if (histRow?.interested_party_id == null) {
        notify?.({
          severity: "warn",
          summary: "Sin detalle",
          detail:
            "Este registro no tiene una parte interesada vinculada para abrir el detalle."
        });
        return;
      }
      const party = parties.find(
        (r) => Number(r.id) === Number(histRow.interested_party_id)
      );
      if (!party) {
        notify?.({
          severity: "warn",
          summary: "No disponible",
          detail:
            "La parte interesada ya no está en este proceso o fue eliminada."
        });
        return;
      }
      onClose();
      navigate(
        "/gestion_HSQ_GCD/interestedParties/interestedPartiesProcessDetail/create",
        {
          state: {
            rowData: party,
            loadData: processRowData,
            mode: canEditFromHistory ? "edit" : "view"
          }
        }
      );
    },
    [
      parties,
      processRowData,
      navigate,
      notify,
      onClose,
      canEditFromHistory
    ]
  );

  const columnsUpdateHistory = useMemo(
    () => [
      { field: "id", header: "Número" },
      { field: "interested_party_name", header: "Parte interesada" },
      { field: "process_name", header: "Proceso" },
      {
        field: "update_description",
        header: "Cambio",
        columnType: "custom",
        render: (r) => {
          const labels = parseChangedFields(r.update_description);
          return (
            <div
              className="table-default-column"
              style={{ whiteSpace: "normal" }}
            >
              {labels.length === 0 ? (
                <p className="m-0">—</p>
              ) : (
                <p className="m-0">{labels.join(", ")}</p>
              )}
            </div>
          );
        }
      },
      {
        field: "responsible_name",
        header: "Responsable",
        columnType: "custom",
        render: (r) => (
          <p className="table-default-column">{r.responsible_name || "—"}</p>
        )
      },
      {
        field: "occurred_display",
        header: "Fecha y hora",
        columnType: "custom",
        render: (r) => {
          const rawDate = r.date ?? r.occurred_date;
          const rawTime = r.time ?? r.occurred_time;
          const datePart = formatUpdateHistoryDatePart(rawDate) || "—";
          const timePart = formatUpdateHistoryTimePart(rawTime) || "—";
          return (
            <div className="table-default-column">
              <p className="m-0">{datePart}</p>
              <p className="m-0" style={{ fontSize: "0.92em", opacity: 0.92 }}>
                {timePart}
              </p>
            </div>
          );
        }
      },
      {
        field: "history_actions",
        header: "",
        columnType: "actionButtons",
        ordenable: false,
        columnWidth: "56px"
      }
    ],
    []
  );

  return (
    <ModalComponent
      open={open}
      handleClose={onClose}
      title="Control de actualización"
      width="95%"
      maxWidth="1100px"
    >
      <div className="filter-container">
        <form>
          <Grid container spacing={1} alignItems="flex-end">
            <Grid item xs={12} md={8}>
              <div className="mid-filter-margin">
                <FormTextField
                  name="historyPartySearch"
                  control={control}
                  label="Filtrar por parte interesada:"
                  margin="0"
                />
              </div>
            </Grid>
            <Grid item xs />
            <Grid item xs={12} md="auto">
              <div className="IHContainer j-end">
                <LaddaButton
                  variant="contained"
                  type="button"
                  onClick={handleApplyFilter}
                  margin="0"
                  width="52px"
                  title="Filtrar"
                >
                  <img
                    className="btn-icon"
                    src={icons.filterWhite}
                    alt="Filtrar"
                  />
                </LaddaButton>
                <LaddaButton
                  variant="contained"
                  type="button"
                  onClick={handleClearFilter}
                  margin="0 0 0 10px"
                  width="52px"
                  title="Borrar filtro"
                >
                  <img
                    className="btn-icon"
                    src={icons.clearFilterWhite}
                    alt="Borrar filtro"
                  />
                </LaddaButton>
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
      <SimpleTable
        data={rows}
        columns={columnsUpdateHistory}
        tableStyle={{ minWidth: "820px" }}
        buttons={[
          {
            type: "icon",
            icon: icons.eye,
            onClick: (histRow) => handleViewPartyFromHistory(histRow)
          }
        ]}
        isPaginated={true}
        serverPagination={true}
        totalRecords={total}
        rowsPerPage={UPDATE_HISTORY_PAGE_SIZE}
        currentPage={page}
        onPageChange={handlePageChange}
        hidePaginatorRowsDropdown
      />
    </ModalComponent>
  );
};

export default InterestedPartiesUpdateHistoryModal;
